/**
 * Soft 80 % warning toast for AI usage quotas.
 *
 * Measurement and interruption are deliberately split. `Ai Quota Warning Shown`
 * fires on every 80 % crossing regardless of enforcement, carrying `enforced`
 * so the two regimes stay separable — three saved PostHog insights consume this
 * event (Gate Pressure Over Time, Gate -> Upgrade Funnel, Upsell Target List),
 * and gating the track() call alongside the toast silently zeroed all three
 * while the funnel merely shrank its denominator instead of breaking loudly.
 * Threshold pressure is also the evidence needed to decide whether to enforce
 * at all, so it must keep flowing exactly while enforcement is off.
 *
 * The toast is the part that interrupts, so it is the part that requires the
 * cap to be real.
 *
 * Mounted once near the app root. Watches the AI quota state from
 * `useAiQuotaUsage` and fires a one-shot toast per session for any
 * feature that crosses 80 % usage. The hard 100 % block is handled
 * separately by the existing 402 interceptor → UpgradePromptModal.
 *
 * Per-session de-dup uses sessionStorage so the same warning doesn't
 * fire on every back-navigation to a workspace.
 */

import { useEffect } from 'react';

import { useAiQuotaUsage, type AiQuotaSummary } from '../hooks/data/useAiQuotaUsage';
import { useAppToast } from '../hooks/ui/useAppToast';
import { useWorkspaceId } from '../contexts/WorkspaceContext';
import { ANALYTICS_EVENTS } from '../constants/analytics';
import { safeAnalytics } from '../utils/analytics';

const SESSION_STORAGE_PREFIX = 'aiQuotaWarning';
const WARNING_THRESHOLD = 80;

const FEATURE_LABELS: Readonly<Record<keyof AiQuotaSummary, string>> = {
  chat: 'AI chat messages',
  liveMinutes: 'live mode minutes',
  textExtractions: 'text extractions (images and PDF)',
  transcriptionMinutes: 'voice transcription minutes',
  codeExecutionMinutes: 'data analysis (code execution) minutes',
};

function sessionKey(workspaceId: string, feature: string): string {
  return `${SESSION_STORAGE_PREFIX}:${workspaceId}:${feature}`;
}

/**
 * Dedup key for the ANALYTICS half. Separate from the toast's key on purpose:
 * sharing one would let a shadow-mode crossing mark the feature warned, so if
 * enforcement flipped on later in the same session the user would never be
 * told before being refused.
 */
function trackedKey(feature: keyof AiQuotaSummary): string {
  return `${feature}:tracked`;
}

function alreadyWarned(workspaceId: string, feature: string): boolean {
  try {
    return sessionStorage.getItem(sessionKey(workspaceId, feature)) === '1';
  } catch {
    // Private mode / sandboxed iframe — sessionStorage may throw. Fail
    // open: if we can't dedup, we'd rather warn extra than miss it.
    return false;
  }
}

function markWarned(workspaceId: string, feature: string): void {
  try {
    sessionStorage.setItem(sessionKey(workspaceId, feature), '1');
  } catch {
    // Best effort.
  }
}

export function AiQuotaWarningProvider({ children }: { readonly children: React.ReactNode }) {
  const workspaceId = useWorkspaceId();
  const { data } = useAiQuotaUsage();
  const toast = useAppToast();

  useEffect(() => {
    if (!data || !workspaceId) return;

    const features = Object.keys(FEATURE_LABELS) as ReadonlyArray<keyof AiQuotaSummary>;
    for (const feature of features) {
      const state = data[feature];
      // Skip unlimited tiers (cap === null → percentUsed === 0) and
      // anything still below the threshold.
      if (state.cap === null) continue;
      if (state.percentUsed < WARNING_THRESHOLD) continue;
      if (state.percentUsed >= 100) continue; // hard block uses the modal

      // `enforced` is optional on the wire — an older backend that omits it
      // reads as "not enforced", which keeps the interruption on the safe side.
      const enforced = state.enforced === true;

      // Measurement first, and unconditional: one per (workspace, feature) per
      // session under its OWN key, so a shadow-mode crossing cannot consume the
      // toast's one-shot budget.
      if (!alreadyWarned(workspaceId, trackedKey(feature))) {
        markWarned(workspaceId, trackedKey(feature));
        safeAnalytics.track(ANALYTICS_EVENTS.AI_QUOTA_WARNING_SHOWN, {
          feature,
          percent_used: state.percentUsed,
          enforced,
        });
      }

      // Interruption second, and only when the cap would actually refuse work.
      if (!enforced) continue;
      if (alreadyWarned(workspaceId, feature)) continue;

      markWarned(workspaceId, feature);
      const label = FEATURE_LABELS[feature];
      toast({
        status: 'warning',
        title: 'Approaching your AI usage limit',
        description: `You've used ${state.percentUsed}% of your ${label} this period. Upgrade to keep going.`,
        duration: 8000,
        isClosable: true,
      });
    }
  }, [data, workspaceId, toast]);

  return <>{children}</>;
}
